import { useState } from 'react'
import { Download, FileJson, Loader2 } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { api } from '@/lib/api'
import { countryLabel } from './constants'

export function DownloadMenu({ batchId, countries }: { batchId: string; countries: string[] }) {
  const [busy, setBusy] = useState(false)
  const [product, setProduct] = useState<string>('all')
  const [adoptedOnly, setAdoptedOnly] = useState(true)

  const download = async () => {
    setBusy(true)
    try {
      const country = product === 'all' ? undefined : product
      const scope = adoptedOnly ? 'adopted' : country ? 'country' : 'all'
      const url = await api.getBatchDownloadUrl(batchId, scope, country)
      window.open(url, '_blank')
    } finally {
      setBusy(false)
    }
  }

  const downloadFeedbackJsonl = async () => {
    setBusy(true)
    try {
      const blob = await api.exportBatchFeedbackJsonl(batchId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'feedback.jsonl'
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Popover>
      <PopoverTrigger className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Download
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3 p-4">
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500">1. Product</label>
          <select
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 bg-white"
          >
            <option value="all">All products</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {countryLabel(country)}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-500">2. Files</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAdoptedOnly(true)}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium border ${
                adoptedOnly
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              ✓ Adopted (采纳)
            </button>
            <button
              type="button"
              onClick={() => setAdoptedOnly(false)}
              className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium border ${
                !adoptedOnly
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              All files
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download zip
        </button>

        <button
          type="button"
          onClick={downloadFeedbackJsonl}
          disabled={busy}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 text-gray-600 text-xs font-medium hover:bg-gray-50 disabled:opacity-60"
        >
          <FileJson className="h-3.5 w-3.5" />
          Feedback .jsonl (for Claude)
        </button>
      </PopoverContent>
    </Popover>
  )
}
